/**
 * マーケ施策管理（T-MK-B）の派生値・純関数ロジック。
 * plan-mk.md §1.4「ルール」を console 側で再実装したもの（stoq-worker
 * `src/mk-initiatives-rules.ts` と値域・生成規則は同一。実装は別ファイル・別プロセス）。
 * I/O無しの純関数のみを置く（fetchはこのファイルから行わない）。
 */
import type {
  ChecklistItem,
  InitiativeChannel,
  InitiativeChecklist,
  InitiativeSource,
  InitiativeStatus,
  SourceGroup,
  SyncResults,
  TaskTag,
} from "../types/contract";

export const CHANNEL_LABEL: Record<InitiativeChannel, string> = {
  line: "LINE",
  meta: "Meta",
  influencer: "Influencer",
  rakuten: "楽天",
  shopify: "Shopify",
};

export const CHANNEL_COLOR: Record<InitiativeChannel, string> = {
  line: "#2e7d5b",
  meta: "#3a5fa8",
  influencer: "#8a5a9e",
  rakuten: "#b3452f",
  shopify: "#6b7f2a",
};

export const CHANNEL_ORDER: InitiativeChannel[] = ["line", "meta", "influencer", "rakuten", "shopify"];

export const STATUS_LABEL: Record<InitiativeStatus, string> = {
  planning: "企画・準備",
  active: "実行中",
  awaiting_human: "人間確認待ち",
  awaiting_measure: "効果測定待ち",
  done: "完了",
  needs_action: "要対応",
};

export const STATUS_COLOR: Record<InitiativeStatus, string> = {
  planning: "#8a8794",
  active: "#2e7d5b",
  awaiting_human: "#3d3a52",
  awaiting_measure: "#c48f3a",
  done: "#9d9aa8",
  needs_action: "#a8442a",
};

export const STATUS_ORDER: InitiativeStatus[] = [
  "planning",
  "active",
  "awaiting_human",
  "awaiting_measure",
  "done",
  "needs_action",
];

export const TASK_TAG_LABEL: Record<TaskTag, string> = {
  review: "レビュー",
  switch: "切替",
  wrapup: "終了処理",
  post: "投稿日",
  measure: "効果測定",
  check: "確認",
  decision: "判断",
  ai_done: "AI実行済み・未確認",
  missing: "設定漏れ",
};

/** タイムライン帯マークに使うタグ（未完了のみ表示・README §1本文）。 */
const TIMELINE_MARK_TAGS: TaskTag[] = ["review", "switch", "wrapup", "post"];

export function isTimelineMarkTag(tag: TaskTag): boolean {
  return TIMELINE_MARK_TAGS.includes(tag);
}

export function markColor(tag: TaskTag, overdue: boolean): string {
  if (overdue) return "#a8442a";
  if (tag === "review") return "#3a5fa8";
  if (tag === "switch") return "#2e7d5b";
  return "#a8442a"; // wrapup / post
}

/** チャネル別チェックリストテンプレ（PRD §11・プロトタイプ TPL。shopifyは楽天と同一）。 */
export const CHECKLIST_TEMPLATES: Record<InitiativeChannel, { before: string[]; during: string[]; after: string[] }> = {
  line: {
    before: ["配信対象の絞り込み", "配信文・リンク", "分岐条件", "配信日時"],
    during: ["配信実績・クリック", "登録解除数", "想定外の分岐"],
    after: ["通常配信への復帰", "シナリオ停止", "成果記録"],
  },
  meta: {
    before: ["予算・入札", "計測タグ・CAPI", "LPの表示確認", "クリエイティブ入稿", "配信設定"],
    during: ["CTR / CPC", "CPA / CVR", "フリークエンシー", "クリエイティブ疲弊"],
    after: ["停止 / 継続の判断", "差替の記録", "学習の保存"],
  },
  influencer: {
    before: ["依頼条件の合意", "商品発送", "投稿日の確定", "クーポン発行", "LP準備"],
    during: ["商品到着の確認", "投稿確認", "コメント対応", "流入の確認"],
    after: ["売上・費用対効果", "二次利用の許諾", "再依頼の判断"],
  },
  rakuten: {
    before: ["価格設定", "在庫", "クーポン設定", "LP・商品ページ", "計測"],
    during: ["アクセス", "CVR・売上", "在庫", "クーポン利用"],
    after: ["価格復帰", "クーポン停止", "通常LP復帰"],
  },
  // shopify は楽天と同一テンプレ（README §11）
  shopify: {
    before: ["価格設定", "在庫", "クーポン設定", "LP・商品ページ", "計測"],
    during: ["アクセス", "CVR・売上", "在庫", "クーポン利用"],
    after: ["価格復帰", "クーポン停止", "通常LP復帰"],
  },
};

export function buildChecklist(channel: InitiativeChannel): InitiativeChecklist {
  const tpl = CHECKLIST_TEMPLATES[channel] ?? CHECKLIST_TEMPLATES.line;
  const mk = (arr: string[]): ChecklistItem[] => arr.map((t) => ({ t, done: false }));
  return { before: mk(tpl.before), during: mk(tpl.during), after: mk(tpl.after) };
}

export function checklistProgress(cl: InitiativeChecklist): { done: number; total: number } {
  const groups: ChecklistItem[][] = [cl.before, cl.during, cl.after];
  let done = 0;
  let total = 0;
  for (const g of groups) {
    total += g.length;
    done += g.filter((c) => c.done).length;
  }
  return { done, total };
}

/** 'YYYY-MM-DD' に days を加算した 'YYYY-MM-DD'（UTC epoch経由・タイムゾーンのブレを避ける）。 */
export function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map((v) => parseInt(v, 10));
  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/** 'YYYY-MM-DD' → 'M/D'（先頭ゼロなし）。 */
export function formatMD(ymd: string): string {
  const parts = ymd.split("-");
  const m = parseInt(parts[1] ?? "1", 10);
  const d = parseInt(parts[2] ?? "1", 10);
  return `${m}/${d}`;
}

const WEEKDAY_JA = ["日", "月", "火", "水", "木", "金", "土"];

export function weekdayJa(ymd: string): string {
  const [y, m, d] = ymd.split("-").map((v) => parseInt(v, 10));
  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  return WEEKDAY_JA[dt.getUTCDay()];
}

interface JstParts {
  date: string;
  time: string;
}

/** 現在時刻をJST基準の 'YYYY-MM-DD' / 'HH:MM' に分解する（実行環境のTZに依存しない）。 */
function nowJstParts(): JstParts {
  const now = new Date();
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(now).reduce<Record<string, string>>((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  // 一部ICU実装ではhour12:falseでも深夜0時台を"24"で返すことがあるため補正する。
  const hour = parts.hour === "24" ? "00" : parts.hour;
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${hour}:${parts.minute}` };
}

export function todayJST(): string {
  return nowJstParts().date;
}

export function nowJSTTime(): string {
  return nowJstParts().time;
}

/**
 * worker実装は at / created_at / updated_at / done_at を 'YYYY-MM-DD HH:MM:SS'（UTC・Zなし。
 * 既存briefsのnowIso規約）で返す。ブラウザの Date コンストラクタはこの形式を「タイムゾーン情報なし」
 * = ローカル時刻として解釈してしまい、JSTでは9時間ずれる。'T'/'Z'/オフセットが無い文字列は
 * 明示的にUTCとして解釈してから扱う（'T'/'Z'/オフセット付きの文字列はそのまま解釈できるので触らない）。
 */
function parseServerAt(at: string): Date {
  if (/T/.test(at) || /Z$/.test(at) || /[+-]\d{2}:\d{2}$/.test(at)) {
    return new Date(at);
  }
  return new Date(`${at.replace(" ", "T")}Z`);
}

/** 現在時刻を 'YYYY-MM-DD HH:MM:SS'（UTC・Zなし）で返す。worker実装（既存briefsのnowIso規約）と
 *  同じ形式。mock の created_at/updated_at/done_at/history.at はこの形式で保持し、実APIとの
 *  表示差異（parseServerAtのパース分岐）を開発時から検知できるようにする。 */
export function nowUtcSql(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(
    d.getUTCMinutes()
  )}:${pad(d.getUTCSeconds())}`;
}

/** サーバーの日時文字列（§上記）をJST 'M/D HH:MM' 表示に整形する。 */
export function formatHistoryAt(at: string): string {
  const d = parseServerAt(at);
  if (Number.isNaN(d.getTime())) return at;
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Tokyo",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(d).reduce<Record<string, string>>((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  const hour = parts.hour === "24" ? "00" : parts.hour;
  return `${parts.month}/${parts.day} ${hour}:${parts.minute}`;
}

/** 期限超過判定（§1.4）: due_date < today、または due_date == today かつ due_time < now。 */
export function isOverdue(due_date: string, due_time: string, today: string, nowTime: string): boolean {
  if (due_date < today) return true;
  if (due_date === today && due_time < nowTime) return true;
  return false;
}

export interface GeneratedTask {
  due_date: string;
  due_time: string;
  title: string;
  tag: TaskTag;
}

/** 施策登録時の自動タスク生成（PRD §10 / §1.4）。既定時刻 12:00。 */
export function genTasks(input: { channel: InitiativeChannel; start_date: string; end_date: string }): GeneratedTask[] {
  const out: GeneratedTask[] = [];
  const push = (due_date: string, title: string, tag: TaskTag) => out.push({ due_date, due_time: "12:00", title, tag });
  if (input.channel === "meta") {
    push(addDaysYmd(input.start_date, 3), "3日レビュー：CTR / CPC / CPA / CVR とクリエイティブ別の差を確認", "review");
    push(addDaysYmd(input.start_date, 7), "7日レビュー：継続 / 停止 / 予算増額の判断", "review");
  }
  if (input.channel === "rakuten" || input.channel === "shopify") {
    push(addDaysYmd(input.end_date, 1), "通常状態へ戻ったか確認（価格・クーポン・LP）", "wrapup");
  }
  if (input.channel === "line") {
    push(addDaysYmd(input.end_date, 1), "通常ステップへの切替を確認", "switch");
  }
  if (input.channel === "influencer") {
    push(input.end_date, "投稿確認：URL取得・クーポン / LP の動作確認", "post");
  }
  push(addDaysYmd(input.end_date, 3), "効果測定：売上 / CPA 等を確定して記録", "measure");
  return out;
}

/** 登録モーダルの自動生成プレビュー文言。 */
export function genPreviewText(channel: InitiativeChannel, startDate: string, endDate: string): string {
  const s = startDate || todayJST();
  const e = endDate && endDate >= s ? endDate : s;
  const gen = genTasks({ channel, start_date: s, end_date: e });
  const list = gen.map((g) => `${formatMD(g.due_date)} ${TASK_TAG_LABEL[g.tag]}`).join("、");
  return `登録時に自動生成：${list}。${CHANNEL_LABEL[channel]} の開始前 / 実行中 / 終了時チェックリストも付与。`;
}

/* ==========================================================================
   Phase 2（T-MK2-B・plan-mk2.md §1/§3）: 外部連携（Meta/LINE/楽天）表示ルール
   ========================================================================== */

/** 連携バッジ（一覧行・Todayカード・今週終了・詳細ヘッダー）に出す文言。manualはバッジ無し。 */
export const SOURCE_BADGE_LABEL: Record<Exclude<InitiativeSource, "manual">, string> = {
  meta: "Meta連携",
  line_broadcast: "LINE連携",
  line_scenario: "LINE連携",
  rakuten_event: "楽天連携",
  rakuten_coupon: "楽天連携",
};

/** 連携ステータス行・sync結果トーストに出す3系統のラベル。 */
export const SOURCE_GROUP_LABEL: Record<SourceGroup, string> = {
  meta: "Meta",
  line: "LINE",
  rakuten: "楽天",
};

export const SOURCE_GROUP_ORDER: SourceGroup[] = ["meta", "line", "rakuten"];

export interface SourceMetaDeadline {
  date: string;
  time: string;
  raw: string;
  step_order?: number;
}

/** source_meta.deadlines（LINE配信/シナリオのみ）を安全に取り出す。無ければ空配列。 */
export function extractSourceDeadlines(meta: Record<string, unknown> | null): SourceMetaDeadline[] {
  if (!meta) return [];
  const raw = (meta as { deadlines?: unknown }).deadlines;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (d): d is SourceMetaDeadline =>
      !!d && typeof d === "object" && typeof (d as SourceMetaDeadline).date === "string" && typeof (d as SourceMetaDeadline).raw === "string"
  );
}

/** 「今すぐ更新」トーストの要約文言を組み立てる。 */
export function summarizeSyncResults(results: SyncResults): string {
  const parts: string[] = [];
  const errors: string[] = [];
  for (const key of SOURCE_GROUP_ORDER) {
    const r = results[key];
    if (!r) continue;
    if (r.error) {
      errors.push(SOURCE_GROUP_LABEL[key]);
      continue;
    }
    parts.push(`${SOURCE_GROUP_LABEL[key]} ${r.created + r.updated}件`);
  }
  if (errors.length > 0) {
    return `連携を更新しました（${parts.join("／") || "更新なし"}）。失敗：${errors.join("・")}`;
  }
  return `連携を更新しました（${parts.join("／") || "更新なし"}）`;
}
